
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePaymentService } from "@/services/paymentService";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { useSubscriptionService } from "@/services/subscriptionService";

export default function PaymentCallback() {
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyPayment } = usePaymentService();
  const { userSubscription } = useSubscriptionService();

  useEffect(() => {
    const verifyTransaction = async () => {
      try {
        // Get transaction reference from localStorage or URL params
        const transactionRef = localStorage.getItem('flw_tx_ref');
        const queryParams = new URLSearchParams(location.search);
        const txRef = queryParams.get('tx_ref') || transactionRef;
        
        if (!txRef) {
          console.error("No transaction reference found");
          setVerifying(false);
          return;
        }
        
        // Verify the payment
        const result = await verifyPayment(txRef);
        setSuccess(result);
        
        if (result && userSubscription) {
          // Use the current subscription details
          setSubscriptionDetails({
            subscriptionPlan: userSubscription.subscription_plan,
            billingCycle: userSubscription.billing_cycle,
            videosPerDay: userSubscription.videos_per_day,
            messagesPerVideo: userSubscription.messages_per_video,
            maxVideoDuration: userSubscription.max_video_duration,
            expiryDate: userSubscription.expiry_date
          });
        }
        
        // Clear the stored reference
        localStorage.removeItem('flw_tx_ref');
      } catch (error) {
        console.error("Payment verification error:", error);
      } finally {
        setVerifying(false);
      }
    };

    verifyTransaction();
  }, [location.search, verifyPayment, userSubscription]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center space-y-6">
        {verifying ? (
          <>
            <div className="flex justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Verifying Payment</h1>
            <p className="text-muted-foreground">
              Please wait while we verify your payment...
            </p>
          </>
        ) : success ? (
          <>
            <div className="flex justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold">Payment Successful!</h1>
            <p className="text-muted-foreground">
              Your subscription has been activated successfully. You can now enjoy all the features of your plan.
            </p>

            {subscriptionDetails && (
              <div className="bg-muted p-4 rounded-md text-left">
                <h3 className="font-semibold text-center mb-3">Subscription Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Plan:</span>
                    <span className="font-medium">{subscriptionDetails.subscriptionPlan}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Billing:</span>
                    <span className="font-medium capitalize">{subscriptionDetails.billingCycle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Daily Video Limit:</span>
                    <span className="font-medium">
                      {subscriptionDetails.videosPerDay >= 999999 ? 'Unlimited' : subscriptionDetails.videosPerDay}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Messages Per Video:</span>
                    <span className="font-medium">
                      {subscriptionDetails.messagesPerVideo >= 999999 ? 'Unlimited' : subscriptionDetails.messagesPerVideo}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Max Video Duration:</span>
                    <span className="font-medium">{subscriptionDetails.maxVideoDuration} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Expires On:</span>
                    <span className="font-medium">
                      {new Date(subscriptionDetails.expiryDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <Button onClick={() => navigate("/workspace")}>
              Go to Workspace
            </Button>
          </>
        ) : (
          <>
            <div className="flex justify-center">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold">Payment Verification Failed</h1>
            <p className="text-muted-foreground">
              We couldn't verify your payment. This might be because the payment is still processing or there was an issue with the transaction.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
                className="w-full"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate("/pricing")} 
                className="w-full"
              >
                Return to Pricing
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
